import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  MapDeviceMarkerCluster,
  MapDeviceMarkerClusterItem,
  MapDeviceMarkerSelectionPosition,
} from '@utils/map-device-marker-cluster.util';
import { format, parseISO } from 'date-fns';
import { DEVICE_MARKER_HEIGHT } from '@utils/device-marker.util';
import { LngLatBounds, Map, NavigationControl, Popup } from 'maplibre-gl';
import { createMap } from 'maplibre-gl-js-amplify';
import { DeviceViewModel } from '../../device-info-list-table/device-info-list-table.component';

type BatteryItem = {
  icon: string | null;
  color: string | null;
  percentage: number | null;
};

const DEVICE_POPUP_OFFSET: [number, number] = [0, -DEVICE_MARKER_HEIGHT];
const SELECTED_DEVICE_POPUP_OFFSET: [number, number] = [
  0,
  -DEVICE_MARKER_HEIGHT * 1.5,
];

@Component({
  selector: 'app-device-info-map',
  standalone: true,
  imports: [],
  templateUrl: './device-info-map.component.html',
  styleUrl: './device-info-map.component.scss',
})
export class DeviceInfoMapComponent implements OnChanges, OnDestroy, OnInit {
  /** デバイス情報 */
  @Input({ required: true }) deviceList: DeviceViewModel[] = [];

  /** 選択されたデバイス */
  @Input({ required: true }) selectedDevice: DeviceViewModel | undefined;

  /** マップのロードが完了した際に通知 */
  @Output() mapLoaded = new EventEmitter<void>();

  /** マップの移動が終了した際に通知 */
  @Output() mapMoveEnd = new EventEmitter<LngLatBounds>();

  /** 選択マーカーを変更した際に通知 */
  @Output() markerChanged = new EventEmitter<DeviceViewModel | undefined>();

  /** 地図インスタンス */
  private map!: Map;

  /** 地図上のデバイスマーカーを管理するクラスター */
  private deviceMarkerCluster:
    | MapDeviceMarkerCluster<DeviceViewModel>
    | undefined;

  /** マーカー選択時に表示されているポップアップ */
  private activePopup: Popup | null = null;
  private activePopupDeviceId: string | undefined;

  /** ホバー時に表示されているポップアップ */
  private hoverPopup: Popup | null = null;
  private retainPopupAfterSelectionClear = false;
  private mapReady = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.mapReady) {
      return;
    }
    if (changes['deviceList'] !== undefined) {
      this.setDeviceLayers(true);
    }
    if (changes['selectedDevice'] !== undefined) {
      this.updateSelectedDevice();
    }
  }

  async ngOnInit(): Promise<void> {
    // 地図を作成
    this.map = await createMap({
      container: 'map',
      center: [139.767, 35.681],
      zoom: 11,
      minZoom: 1,
    });
    this.map.addControl(new NavigationControl({}));
    this.map.on('moveend', () => this.mapMoveEnd.emit(this.map.getBounds()));

    await this.waitUntilMapReady();
    this.deviceMarkerCluster = new MapDeviceMarkerCluster(this.map, {
      idPrefix: 'device-info-current-location',
      onPointClick: (item) => this.handleMarkerClick(item.data),
      onPointMouseEnter: (item) => this.handleMarkerMouseEnter(item.data),
      onPointMouseLeave: () => this.handleMarkerMouseLeave(),
      onSelectionPositionChanged: (position) =>
        this.handleSelectionPositionChanged(position),
    });
    await this.deviceMarkerCluster.initialize();
    this.mapReady = true;
    this.setDeviceLayers(true);
    this.updateSelectedDevice();
    this.mapLoaded.emit();
  }

  ngOnDestroy(): void {
    this.deviceMarkerCluster?.destroy();
    this.removeActivePopup();
    this.hoverPopup?.remove();
  }

  /** 地図がロードされるまで待機する。 */
  private async waitUntilMapReady(): Promise<void> {
    if (this.map.loaded()) {
      return;
    }
    await new Promise<void>((resolve) =>
      this.map.once('load', () => resolve()),
    );
  }

  /**
   * 地図上にマーカーを表示する。
   * @param pan PANするかどうか
   */
  private setDeviceLayers(pan: boolean): void {
    const items = this.createClusterItems(this.deviceList);
    // マーカー表示
    this.deviceMarkerCluster?.setItems(items);

    // PANする
    if (pan) {
      this.panToItems(items);
    }
  }

  private createClusterItems(
    deviceList: DeviceViewModel[],
  ): MapDeviceMarkerClusterItem<DeviceViewModel>[] {
    return deviceList.flatMap((device) => {
      const longitude = device.location?.longitude;
      const latitude = device.location?.latitude;
      // 位置情報がない場合はマーカーを表示しないようにガード
      if (
        longitude === undefined ||
        latitude === undefined ||
        !Number.isFinite(longitude) ||
        !Number.isFinite(latitude)
      ) {
        return [];
      }
      return [{ id: device.serialNo, longitude, latitude, data: device }];
    });
  }

  private updateSelectedDevice(): void {
    // テーブルのデバイスを選択したときの挙動
    // ピンの再クリックによる選択解除だけは、mouseleaveまで表示中のポップアップを残す。
    const retainPopup =
      this.retainPopupAfterSelectionClear && this.selectedDevice === undefined;
    if (!retainPopup) {
      this.retainPopupAfterSelectionClear = false;
      const popupMatchesSelectedDevice =
        this.activePopup !== null &&
        this.selectedDevice !== undefined &&
        this.activePopupDeviceId === this.selectedDevice.serialNo;
      if (!popupMatchesSelectedDevice) {
        this.removeActivePopup();
      }
      this.clearHoverPopup();
    }
    this.deviceMarkerCluster?.setSelectedId(this.selectedDevice?.serialNo);
  }

  private handleSelectionPositionChanged(
    position: MapDeviceMarkerSelectionPosition | undefined,
  ): void {
    if (!this.selectedDevice || !position) {
      if (!this.retainPopupAfterSelectionClear) {
        this.removeActivePopup();
      }
      return;
    }
    // 選択デバイスがクラスター内なら、個別座標ではなくクラスター中心へポップアップを追従させる。
    this.showPopupForDevice(
      this.selectedDevice,
      position.longitude,
      position.latitude,
      SELECTED_DEVICE_POPUP_OFFSET,
    );
  }

  /**
   * マーカークリック時のポップアップ表示状態を制御する
   * @param device デバイス情報
   */
  private handleMarkerClick(device: DeviceViewModel): void {
    if (this.selectedDevice?.serialNo === device.serialNo) {
      // 選択状態のマーカーがクリックされた場合
      // 親から未選択状態が返るまでの間も、再クリックしたピンのポップアップを維持する。
      this.retainPopupAfterSelectionClear = true;
      // 選択状態を解除
      this.markerChanged.emit(undefined);
      this.clearHoverPopup();
      return;
    }

    // 非選択状態のマーカーがクリックされた場合
    // 一度選択状態をすべて解除する
    if (this.selectedDevice) {
      // SelectionModelの単一選択を維持するため、別デバイスの選択前に現在の選択を解除する。
      this.markerChanged.emit(undefined);
    }
    // 新しいデバイスを選択状態にする
    this.markerChanged.emit(device);
    this.showPopupForDevice(
      device,
      device.location!.longitude,
      device.location!.latitude,
    );
    this.clearHoverPopup();
  }

  /**
   * マーカーホバー時のポップアップ表示状態を制御する
   * @param device デバイス情報
   */
  private handleMarkerMouseEnter(device: DeviceViewModel): void {
    if (this.selectedDevice?.serialNo === device.serialNo) {
      return;
    }
    const longitude = device.location?.longitude;
    const latitude = device.location?.latitude;
    if (longitude === undefined || latitude === undefined) {
      return;
    }
    this.clearHoverPopup();
    this.hoverPopup = this.createPopup(
      device,
      longitude,
      latitude,
      DEVICE_POPUP_OFFSET,
    );
  }

  private handleMarkerMouseLeave(): void {
    this.clearHoverPopup();
    if (!this.retainPopupAfterSelectionClear) {
      return;
    }
    this.retainPopupAfterSelectionClear = false;
    this.removeActivePopup();
  }

  /**
   * 指定されたデバイスの内容のポップアップを指定の座標に表示する
   * @param device デバイス情報
   * @param longitude 経度
   * @param latitude 緯度
   * @param offset ポップアップの表示位置
   */
  private showPopupForDevice(
    device: DeviceViewModel,
    longitude: number,
    latitude: number,
    offset: [number, number] = DEVICE_POPUP_OFFSET,
  ): void {
    if (this.activePopup && this.activePopupDeviceId === device.serialNo) {
      this.activePopup
        .setLngLat({ lon: longitude, lat: latitude })
        .setHTML(this.createPointDescription(device))
        .setOffset(offset);
      return;
    }

    this.removeActivePopup();
    this.activePopup = this.createPopup(device, longitude, latitude, offset);
    this.activePopupDeviceId = device.serialNo;
  }

  private removeActivePopup(): void {
    this.activePopup?.remove();
    this.activePopup = null;
    this.activePopupDeviceId = undefined;
  }

  private createPopup(
    device: DeviceViewModel,
    longitude: number,
    latitude: number,
    offset: [number, number],
  ): Popup {
    return new Popup({ closeButton: false, closeOnClick: false })
      .setLngLat({ lon: longitude, lat: latitude })
      .setHTML(this.createPointDescription(device))
      .setOffset(offset)
      .addTo(this.map);
  }

  private clearHoverPopup(): void {
    this.hoverPopup?.remove();
    this.hoverPopup = null;
  }

  /**
   * ポップアップに表示する説明内容を作成する。
   * @param device デバイス情報
   * @returns ポップアップに表示する説明内容
   */
  private createPointDescription(device: DeviceViewModel): string {
    // バッテリーの表示内容を取得
    const batteryStatus = this.getBatteryStatus(device.battery);

    // バッテリーの表示内容（文字列）を設定
    const batteryIcon = batteryStatus.icon
      ? `<span class="material-symbols-outlined">${batteryStatus.icon}</span>`
      : '';
    const batteryPercentage =
      batteryStatus.percentage != null && batteryStatus.percentage >= 0
        ? `${batteryStatus.percentage}%`
        : '';

    // ポップアップ表示内容の設定
    return `
      <div>デバイス名：${device.deviceName}</div>
      <div>シリアル番号：${device.serialNo}</div>
      <div>デバイス種類：${device.deviceType}</div>
      <div>最終接続日時：${device.lastCommunicationAt ? format(parseISO(device.lastCommunicationAt), 'yyyy/MM/dd hh:mm:ss') : ''}</div>
      <div>ステータス：${device.status ?? ''}</div>
      <div>住所：${device.location?.address ?? ''}</div>
      <div style="display: flex; align-items: center">
        <span>バッテリー：</span>
        <span style="display: inline-flex; vertical-align: middle; color: ${batteryStatus.color}">${batteryIcon}</span>
        <span>${batteryPercentage}</span>
      </div>
      <div>設定グループ：${device.settingGroupName ?? ''}</div>
    `;
  }

  /**
   * バッテリーアイコンの表示内容を取得する。
   * @param batteryPercentage バッテリー残量
   * @returns BatteryItem
   */
  private getBatteryStatus(batteryPercentage: number | undefined): BatteryItem {
    if (batteryPercentage == null) {
      return { icon: null, color: null, percentage: null };
    }

    const batteryItems = [
      { icon: 'battery_full_alt', color: 'rgb(34 197 94)', minLevel: 76 },
      { icon: 'battery_horiz_075', color: 'rgb(34 197 94)', minLevel: 51 },
      { icon: 'battery_horiz_050', color: 'rgb(34 197 94)', minLevel: 26 },
      { icon: 'battery_low', color: 'rgb(234 179 8)', minLevel: 11 },
      { icon: 'battery_very_low', color: 'rgb(239 68 68)', minLevel: 0 },
    ];

    const status = batteryItems.find(
      (item) => batteryPercentage >= item.minLevel,
    );
    return status
      ? {
          icon: status.icon,
          color: status.color,
          percentage: batteryPercentage,
        }
      : { icon: null, color: null, percentage: null };
  }

  /**
   * 地図上に表示されているマーカーが見えるように地図をPANする。
   * @param items マーカーとして表示するデバイス位置
   */
  private panToItems(
    items: MapDeviceMarkerClusterItem<DeviceViewModel>[],
  ): void {
    if (items.length < 1) {
      this.map.setCenter([139.767, 35.681]);
      this.map.setZoom(11);
      return;
    }
    // マーカーの座標を包括する範囲を計算
    const bounds = new LngLatBounds();
    items.forEach((item) => bounds.extend([item.longitude, item.latitude]));

    // マップをPANしてマーカーが見えるようにする
    this.map.fitBounds(bounds, { padding: 50, maxZoom: 15 });
  }
}
